import { Fragment, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Download, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

function buildCsv(gb) {
  const sims = gb.simulators;
  const header = [
    "Name", "Email", "Sims attempted", "Sims total", "Competent", "Best score",
    "Total attempts", "Last active", ...sims.map((s) => s.name),
  ];
  const rows = gb.learners.map((l) => [
    l.name, l.email, l.sims_attempted, gb.total_sims, l.competent_count,
    l.best_overall ?? "", l.attempts_total,
    l.last_active ? new Date(l.last_active).toISOString().slice(0, 10) : "",
    ...sims.map((s) => (l.sims[s.id] ? l.sims[s.id].best : "")),
  ]);
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
}

export function TeamGradebook({ teamId, teamName }) {
  const [gb, setGb] = useState(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setGb(null); setError(false);
    api.get(`/api/team/${teamId}/gradebook`)
      .then(({ data }) => setGb(data))
      .catch(() => setError(true));
  }, [teamId]);

  const download = () => {
    const csv = buildCsv(gb);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(teamName || "team").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-gradebook.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return <div className="text-sm text-rose-500 py-4" data-testid="gradebook-error">Couldn't load the gradebook.</div>;
  if (!gb) return <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading gradebook…</div>;

  return (
    <div data-testid="team-gradebook">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs text-slate-500">
          Best score per simulator · competent = score ≥ {gb.pass_threshold}%. {gb.learners.length} learner{gb.learners.length !== 1 ? "s" : ""}.
        </p>
        <Button size="sm" variant="outline" onClick={download} disabled={gb.learners.length === 0} data-testid="gradebook-csv-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {gb.learners.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg" data-testid="gradebook-empty">
          No learners in this team yet.
        </div>
      ) : (
        <>
        {/* Mobile: stacked learner cards */}
        <div className="md:hidden space-y-2" data-testid="gradebook-mobile">
          {gb.learners.map((l) => {
            const open = expanded === l.user_id;
            return (
              <div key={l.user_id} className="rounded-lg border border-slate-200 bg-white" data-testid={`gradebook-card-${l.user_id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : l.user_id)}
                  className="w-full text-left p-3 flex items-start gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 truncate">{l.name || l.email}</div>
                    {l.name && <div className="text-xs text-slate-400 truncate">{l.email}</div>}
                    <div className="mt-2 grid grid-cols-4 gap-1.5 text-[11px]">
                      <div className="flex flex-col">
                        <span className="text-[9px] uppercase tracking-wider text-slate-400">Done</span>
                        <span className="font-semibold text-slate-700">{l.sims_attempted}/{gb.total_sims}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[9px] uppercase tracking-wider text-slate-400">Comp</span>
                        <span className="font-semibold text-emerald-600">{l.competent_count}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[9px] uppercase tracking-wider text-slate-400">Best</span>
                        <span className="font-semibold text-slate-900">{l.best_overall ?? "—"}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[9px] uppercase tracking-wider text-slate-400">Att.</span>
                        <span className="font-semibold text-slate-700">{l.attempts_total}</span>
                      </div>
                    </div>
                    <div className="mt-1.5 text-[10px] text-slate-500">Last active: {fmtDate(l.last_active)}</div>
                  </div>
                  <span className="text-slate-400 shrink-0 mt-0.5">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
                </button>
                {open && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-3" data-testid={`gradebook-card-detail-${l.user_id}`}>
                    {l.sims_attempted === 0 ? (
                      <div className="text-xs text-slate-400">No simulator attempts yet.</div>
                    ) : (
                      <div className="grid grid-cols-1 gap-1.5">
                        {gb.simulators.filter((s) => l.sims[s.id]).map((s) => {
                          const d = l.sims[s.id];
                          const pass = d.best >= gb.pass_threshold;
                          return (
                            <div key={s.id} className="flex items-center justify-between gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                              <span className="truncate text-slate-700">{s.name}</span>
                              <span className="flex items-center gap-2 shrink-0">
                                <span className={`font-semibold ${pass ? "text-emerald-600" : "text-slate-500"}`}>{d.best}</span>
                                {pass && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                                <span className="text-slate-400">×{d.attempts}</span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop/tablet: table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="gradebook-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3">Learner</th>
                <th className="py-2 px-2 text-center">Completed</th>
                <th className="py-2 px-2 text-center">Competent</th>
                <th className="py-2 px-2 text-center">Best</th>
                <th className="py-2 px-2 text-center">Attempts</th>
                <th className="py-2 px-2">Last active</th>
                <th className="py-2 w-6"></th>
              </tr>
            </thead>
            <tbody>
              {gb.learners.map((l) => {
                const open = expanded === l.user_id;
                return (
                  <Fragment key={l.user_id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setExpanded(open ? null : l.user_id)}
                        data-testid={`gradebook-row-${l.user_id}`}>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-slate-800">{l.name || l.email}</div>
                        {l.name && <div className="text-xs text-slate-400">{l.email}</div>}
                      </td>
                      <td className="py-2 px-2 text-center text-slate-600">{l.sims_attempted}/{gb.total_sims}</td>
                      <td className="py-2 px-2 text-center font-semibold text-emerald-600">{l.competent_count}</td>
                      <td className="py-2 px-2 text-center font-semibold text-slate-900">{l.best_overall ?? "—"}</td>
                      <td className="py-2 px-2 text-center text-slate-600">{l.attempts_total}</td>
                      <td className="py-2 px-2 text-slate-500">{fmtDate(l.last_active)}</td>
                      <td className="py-2 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-50/60" data-testid={`gradebook-detail-${l.user_id}`}>
                        <td colSpan={7} className="py-3 px-3">
                          {l.sims_attempted === 0 ? (
                            <div className="text-xs text-slate-400">No simulator attempts yet.</div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              {gb.simulators.filter((s) => l.sims[s.id]).map((s) => {
                                const d = l.sims[s.id];
                                const pass = d.best >= gb.pass_threshold;
                                return (
                                  <div key={s.id} className="flex items-center justify-between gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                                    <span className="truncate text-slate-700">{s.name}</span>
                                    <span className="flex items-center gap-2 shrink-0">
                                      <span className={`font-semibold ${pass ? "text-emerald-600" : "text-slate-500"}`}>{d.best}</span>
                                      {pass && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                                      <span className="text-slate-400">×{d.attempts}</span>
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

export default TeamGradebook;
